import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import {
  extractFileHeaders,
  suggestTableByHeaders,
  suggestTableByFileName,
  type TableSuggestion,
} from "@/lib/tableMatcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Table2, Sparkles } from "lucide-react";

interface SyncUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  upload: {
    id: string;
    file_name: string;
    file_path: string;
    file_type: string | null;
  } | null;
  onSyncStarted: () => void;
}

const SyncUploadDialog = ({ open, onOpenChange, upload, onSyncStarted }: SyncUploadDialogProps) => {
  const { toast } = useToast();
  const { markHasData } = useAuth();
  const { tables, schemaData, isLoading: schemaLoading, refetch } = useMcpSchema();

  const [selectedTable, setSelectedTable] = useState<string>("");
  const [showNewTable, setShowNewTable] = useState(false);
  const [newTableName, setNewTableName] = useState("");
  const [creatingTable, setCreatingTable] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [suggestion, setSuggestion] = useState<TableSuggestion | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestRanRef = useRef<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Auto-suggest table when dialog opens
  useEffect(() => {
    if (!open || !upload || schemaLoading || schemaData.length === 0) return;

    const key = upload.file_path;
    if (suggestRanRef.current === key) return;
    suggestRanRef.current = key;

    const run = async () => {
      setSuggestLoading(true);
      try {
        // Download file to extract headers
        const { data: fileData } = await supabase.storage
          .from("manual-uploads")
          .download(upload.file_path);

        if (fileData) {
          const headers = await extractFileHeaders(fileData, upload.file_name);
          if (headers.length > 0) {
            const match = suggestTableByHeaders(headers, schemaData);
            if (match) {
              setSuggestion(match);
              setSelectedTable(match.tableName);
              setSuggestLoading(false);
              return;
            }
          }
        }

        // Fallback: file name matching
        const fileNameMatch = suggestTableByFileName(upload.file_name, tables);
        if (fileNameMatch) {
          setSuggestion({ tableName: fileNameMatch, score: 0, matchedColumns: 0, totalFileHeaders: 0 });
          setSelectedTable(fileNameMatch);
        }
      } catch {
        // Suggestion is best-effort, don't block on errors
      }
      setSuggestLoading(false);
    };

    run();
  }, [open, upload, schemaLoading, schemaData, tables]);

  const handleCreateTable = async () => {
    if (!newTableName.trim()) {
      toast({ title: "Error", description: "Table name is required.", variant: "destructive" });
      return;
    }

    setCreatingTable(true);
    try {
      const headers = await buildEdgeFunctionHeaders();
      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/createSchema`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          table_name: newTableName.trim(),
          columns: [{ name: "ida", type: "string" }],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to create table" }));
        throw new Error(err.error || err.detail || `Request failed (${res.status})`);
      }

      toast({ title: "Table created", description: `"${newTableName.trim()}" created successfully.` });
      setSelectedTable(newTableName.trim());
      setNewTableName("");
      setShowNewTable(false);
      setSuggestion(null);
      await refetch();
    } catch (err: any) {
      toast({ title: "Create failed", description: err.message, variant: "destructive" });
    }
    setCreatingTable(false);
  };

  const handleSync = async () => {
    if (!selectedTable || !upload) return;

    setSyncing(true);
    try {
      // Download the file from Supabase storage
      const { data: fileData, error: downloadError } = await supabase.storage
        .from("manual-uploads")
        .download(upload.file_path);

      if (downloadError || !fileData) throw new Error(downloadError?.message || "Failed to download file");

      const formData = new FormData();
      formData.append("table_name", selectedTable);
      formData.append("batch_size", "1000");
      formData.append("file", fileData, upload.file_name);

      const fileHeaders = await buildEdgeFunctionHeaders({ contentType: null });
      const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/ingestRecords/file`, {
        method: "POST",
        headers: fileHeaders,
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Sync failed" }));
        throw new Error(err.error || err.detail || `Request failed (${res.status})`);
      }

      const result = await res.json().catch(() => ({}));

      // Update the manual_upload record with sync status
      await supabase
        .from("manual_upload")
        .update({
          sync_status: "synced",
          table_name: selectedTable,
          row_count: result.records_inserted ?? result.row_count ?? null,
          error_message: null,
        } as any)
        .eq("id", upload.id);

      toast({ title: "Sync complete", description: `File synced to "${selectedTable}" successfully.` });
      markHasData();
      onSyncStarted();
      onOpenChange(false);
    } catch (err: any) {
      // Update status to failed
      await supabase
        .from("manual_upload")
        .update({
          sync_status: "failed",
          error_message: err.message,
        } as any)
        .eq("id", upload.id);

      toast({ title: "Sync failed", description: err.message, variant: "destructive" });
      onSyncStarted();
    }
    setSyncing(false);
  };

  const resetState = () => {
    setSelectedTable("");
    setShowNewTable(false);
    setNewTableName("");
    setSuggestion(null);
    suggestRanRef.current = null;
  };

  const suggestionHint = suggestion
    ? suggestion.matchedColumns > 0
      ? `Suggested: "${suggestion.tableName}" — ${suggestion.matchedColumns}/${suggestion.totalFileHeaders} columns matched`
      : `Suggested: "${suggestion.tableName}" — file name match`
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetState();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Table2 className="h-5 w-5" />
            Sync to Table
          </DialogTitle>
          <DialogDescription>
            Select a target table or create a new one, then sync{" "}
            <span className="font-medium">{upload?.file_name}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Table selection */}
          <div className="space-y-2">
            <Label>Target Table</Label>
            {schemaLoading || suggestLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {suggestLoading ? "Analyzing file headers…" : "Loading tables…"}
              </div>
            ) : (
              <>
                <Select value={selectedTable} onValueChange={(v) => { setSelectedTable(v); setSuggestion(null); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a table" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suggestionHint && selectedTable === suggestion?.tableName && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Sparkles className="h-3 w-3 text-amber-500" />
                    {suggestionHint}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Create new table */}
          {!showNewTable ? (
            <Button variant="outline" size="sm" className="gap-1" onClick={() => setShowNewTable(true)}>
              <Plus className="h-3.5 w-3.5" />
              Create New Table
            </Button>
          ) : (
            <div className="space-y-2 rounded-md border p-3">
              <Label>New Table Name</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g. sales_data"
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  disabled={creatingTable}
                />
                <Button onClick={handleCreateTable} disabled={creatingTable || !newTableName.trim()} size="sm">
                  {creatingTable ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowNewTable(false)} className="text-xs">
                Cancel
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button onClick={handleSync} disabled={!selectedTable || syncing}>
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Start Syncing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncUploadDialog;
